import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import style from 'ember-style-modifier/modifiers/style';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import Popover from 'tio-ui/components/popover';
import CurrencySuperscriptCents from '../currency-superscript-cents.gts';
import { formatISODateToDisplay } from '../../utils/date';
import type { IntlService } from 'ember-intl';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type {
  BenefitPeriodCardModel,
  BenefitPeriodSegment,
} from '../../utils/tuition-assistance/limit-summary';

interface BenefitPeriodCardSignature {
  Args: {
    model: BenefitPeriodCardModel;
    externalSpendLabel: string;
    /** Period heading, e.g. "current year". Omit for the single dashboard card. */
    label?: string;
    /** 'lg' is the dashboard hero treatment; default 'sm' matches the history grid cards. */
    size?: 'sm' | 'lg';
    showOverLimitBanner?: boolean;
    /** Enables the partner popover on the external legend entry. */
    employee?: EmployeeModel;
  };
  Element: HTMLDivElement;
}

export default class BenefitPeriodCard extends Component<BenefitPeriodCardSignature> {
  @service declare intl: IntlService;

  get model() {
    return this.args.model;
  }

  get isLarge() {
    return this.args.size === 'lg';
  }

  get headlineClass() {
    return this.isLarge
      ? 'tio-text-3xl-bold tio-fg-primary'
      : 'tio-text-2xl-semibold tio-fg-primary mt-2';
  }

  get barHeightClass() {
    return this.isLarge ? 'h-3' : 'h-2';
  }

  get minVisiblePercentage() {
    return 5;
  }

  /** The bar needs a positive denominator to mean anything. */
  get hasBar() {
    return this.model.eligibleUpToCents != null && this.model.eligibleUpToCents > 0;
  }

  get showDates() {
    return Boolean(this.model.annualStartDate && this.model.annualEndDate);
  }

  get annualStartDateDisplay() {
    return this.model.annualStartDate ?? '';
  }

  // The window end is exclusive (it is also the next window's start), so the
  // displayed benefit year ends one day earlier; the raw value stays on the
  // model for the next-period fetch.
  get annualEndDateDisplay() {
    const raw = this.model.annualEndDate;
    if (!raw) return '';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    parsed.setUTCDate(parsed.getUTCDate() - 1);
    return parsed.toISOString().slice(0, 10);
  }

  get hasEligibleUpTo() {
    return this.model.eligibleUpToCents !== null;
  }

  get displayTotalUsed() {
    return Math.max(0, this.model.totalUsedCents);
  }

  get totalUsedPercentage() {
    const limit = this.model.eligibleUpToCents;
    const totalUsed = this.model.totalUsedBarCents;
    if (!limit || limit <= 0 || totalUsed <= 0) return 0;
    const rawPercentage = (totalUsed / limit) * 100;
    return Math.min(100, Math.max(this.minVisiblePercentage, rawPercentage));
  }

  segmentPercentage = (segment: BenefitPeriodSegment) => {
    const totalUsed = this.model.totalUsedBarCents;
    if (totalUsed <= 0 || segment.barAmountCents <= 0) return 0;
    return (segment.barAmountCents / totalUsed) * this.totalUsedPercentage;
  };

  segmentColorClass = (segment: BenefitPeriodSegment) => {
    if (segment.key === 'ta') return 'app-series-brand';
    if (segment.key === 'slr') return 'app-series-success';
    return 'app-series-info';
  };

  // TA-only cards keep the historical "paid" label; the product-qualified
  // labels only appear when SLR shares the card (a pooled company cap).
  segmentLabel = (segment: BenefitPeriodSegment) => {
    if (segment.key === 'ta') {
      return this.model.combinedWithSlr
        ? (this.intl.t('tas.dashboard.annual_benefits.tuition_assistance_paid') as string)
        : (this.intl.t('tas.dashboard.annual_benefits.paid') as string);
    }
    if (segment.key === 'slr') {
      return this.intl.t('tas.dashboard.annual_benefits.student_loan_repayment_paid') as string;
    }
    return this.args.externalSpendLabel;
  };

  formatAmount = (cents: number) => {
    return this.intl.formatNumber(cents / 100, { style: 'currency', currency: 'USD' });
  };

  get showOverLimitBanner() {
    return Boolean(this.args.showOverLimitBanner && this.model.isOverLimit);
  }

  // === Partner popover (external legend entry) ===

  get partnerName() {
    return this.args.employee?.company?.name || '';
  }

  get partnerUrlString() {
    return this.args.employee?.company?.companySetting?.partnerUrlString || '';
  }

  get hasPartnerUrl() {
    return Boolean(this.partnerUrlString);
  }

  get hasPartnerContext() {
    return Boolean(this.args.employee && this.partnerName);
  }

  <template>
    <div class="tio-col-loose" data-test-benefit-period-card ...attributes>
      {{#if @label}}
        <div class="tio-text-sm-semibold tio-fg-muted" data-test-benefit-period-label>
          {{@label}}
        </div>
      {{/if}}

      {{#if this.showOverLimitBanner}}
        <AlertBanner @intent="warning" data-test-benefit-period-over-limit>
          {{t "tas.dashboard.annual_benefits.over_limit"}}
        </AlertBanner>
      {{/if}}

      {{#if (eq this.model.mode "empty")}}
        <div class="tio-text-caption tio-fg-muted mt-2" data-test-benefit-period-empty>
          {{t "tas.dashboard.annual_benefits.no_active_eligibility"}}
        </div>
      {{else}}
        {{! Headline: remaining, or total used when no limit bounds the benefit }}
        <div class="tio-col-extra-tight">
          {{#if (eq this.model.mode "remaining")}}
            <div class={{this.headlineClass}} data-test-benefit-period-remaining>
              <CurrencySuperscriptCents @amountCents={{this.model.displayRemainingCents}} />
            </div>
            <div class="tio-text-caption">
              {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
            </div>
          {{else}}
            <div class={{this.headlineClass}} data-test-benefit-period-total-used>
              <CurrencySuperscriptCents @amountCents={{this.displayTotalUsed}} />
            </div>
            <div class="tio-text-caption" data-test-benefit-period-no-limit>
              {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
            </div>
          {{/if}}
        </div>

        {{! Segmented progress bar: TA / SLR / external }}
        {{#if this.hasBar}}
          <div
            class="w-full {{this.barHeightClass}} tio-bg-track rounded-full overflow-hidden flex"
            data-test-benefit-period-bar
          >
            {{#each this.model.segments as |segment|}}
              {{#if (this.segmentPercentage segment)}}
                <Popover
                  @placement="top"
                  @triggerMode="hover"
                  @maxWidth={{200}}
                  @closeOnScroll={{false}}
                >
                  <:trigger as |p|>
                    <div
                      class="h-full cursor-pointer {{this.segmentColorClass segment}}"
                      {{style width=(concat (this.segmentPercentage segment) "%")}}
                      role="button"
                      tabindex="0"
                      aria-label="{{this.segmentLabel segment}}: {{this.formatAmount
                        segment.rawAmountCents
                      }}"
                      data-test-benefit-period-segment={{segment.key}}
                      {{p.register}}
                      {{p.hoverHandlers}}
                      {{on "click" p.toggle}}
                    ></div>
                  </:trigger>
                  <:body>
                    <span class="font-semibold">{{this.segmentLabel segment}}</span>:
                    {{this.formatAmount segment.rawAmountCents}}
                  </:body>
                </Popover>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}

        {{! Legend }}
        {{#if this.model.segments.length}}
          <div class="tio-row-flush flex-wrap gap-x-4 gap-y-1 tio-text-caption">
            {{#each this.model.segments as |segment|}}
              {{#if (eq segment.key "external")}}
                {{#if this.hasPartnerContext}}
                  <Popover>
                    <:trigger as |pop|>
                      <Button
                        @appearance="minimal"
                        class="tio-row-snug hover:underline cursor-pointer"
                        data-test-benefit-period-legend-external
                        {{pop.register}}
                        {{on "click" pop.toggle}}
                      >
                        <span
                          class="w-2 h-2 rounded-full {{this.segmentColorClass segment}}"
                        ></span>
                        <span
                          class="tio-fg-secondary underline decoration-dashed underline-offset-2"
                        >
                          {{this.segmentLabel segment}}
                        </span>
                        <span class="tio-fg-muted">
                          ({{this.formatAmount segment.rawAmountCents}})
                        </span>
                      </Button>
                    </:trigger>
                    <:body>
                      <div class="p-4 min-w-64">
                        {{#if this.hasPartnerUrl}}
                          <a
                            href={{this.partnerUrlString}}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="tio-anchor"
                          >{{this.partnerName}}</a>
                        {{else}}
                          <span class="tio-fg-secondary font-medium">{{this.partnerName}}</span>
                        {{/if}}
                        <div class="text-sm tio-fg-muted">
                          {{t
                            "tas.dashboard.annual_benefits.external_usage_description"
                            partner=this.partnerName
                          }}
                        </div>
                      </div>
                    </:body>
                  </Popover>
                {{else}}
                  <div class="tio-row-snug" data-test-benefit-period-legend={{segment.key}}>
                    <span class="w-2 h-2 rounded-full {{this.segmentColorClass segment}}"></span>
                    <span class="tio-fg-secondary">{{this.segmentLabel segment}}</span>
                    <span class="tio-fg-muted">
                      ({{this.formatAmount segment.rawAmountCents}})
                    </span>
                  </div>
                {{/if}}
              {{else}}
                <div class="tio-row-snug" data-test-benefit-period-legend={{segment.key}}>
                  <span class="w-2 h-2 rounded-full {{this.segmentColorClass segment}}"></span>
                  <span class="tio-fg-secondary">{{this.segmentLabel segment}}</span>
                  <span class="tio-fg-muted">
                    ({{this.formatAmount segment.rawAmountCents}})
                  </span>
                </div>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}

        {{! Benefit year window + eligible up to }}
        <div class="tio-row-between flex-wrap gap-y-1 tio-text-caption">
          {{#if this.showDates}}
            <span class="font-semibold" data-test-benefit-period-dates>
              {{formatISODateToDisplay this.annualStartDateDisplay this.intl "2-digit"}}
              -
              {{formatISODateToDisplay this.annualEndDateDisplay this.intl "2-digit"}}
            </span>
          {{/if}}
          <span data-test-benefit-period-eligible-up-to>
            {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
            <span class="font-semibold">
              {{#if this.hasEligibleUpTo}}
                <CurrencySuperscriptCents @amountCents={{this.model.eligibleUpToCents}} />
              {{else}}
                {{t "tas.dashboard.annual_benefits.no_limit"}}
              {{/if}}
            </span>
            {{#if this.model.hasUnboundedProduct}}
              {{#if this.hasEligibleUpTo}}
                <span class="tio-fg-muted" data-test-benefit-period-unbounded-hint>
                  {{t "tas.dashboard.annual_benefits.plus_unlimited"}}
                </span>
              {{/if}}
            {{/if}}
            {{#if this.model.coverageVariesByProgram}}
              <span class="tio-fg-muted" data-test-benefit-period-coverage-varies>
                &middot;
                {{t "tas.dashboard.annual_benefits.varies_by_program"}}
              </span>
            {{/if}}
          </span>
        </div>
      {{/if}}
    </div>
  </template>
}
